
import { useTranslation } from 'react-i18next';
import { AgeGroup, ChatEvent, ChatEventType } from '@/types';

export type EventVariant = 'warning' | 'error' | 'info';

export interface ResolvedEventMessage {
  title: string;
  note?: string;
  variant: EventVariant;
}

const VARIANT_BY_TYPE: Record<ChatEventType, EventVariant> = {
  rate_limit_hourly: 'warning',
  rate_limit_monthly: 'warning',
  content_filter_input: 'info',
  content_filter_output: 'info',
  guardrails: 'info',
  generation_error: 'error',
};

const ageGroupKey = (ageGroup: AgeGroup): 'preschool' | 'elementary' | 'teen' => {
  if (ageGroup === AgeGroup.PRESCHOOL) return 'preschool';
  if (ageGroup === AgeGroup.ELEMENTARY) return 'elementary';
  return 'teen';
};

const formatResetTime = (resetAt: number | null | undefined, locale: string): string => {
  if (!resetAt) return '';
  const date = new Date(resetAt * 1000);
  const intlLocale = locale.startsWith('ja') ? 'ja-JP' : locale;
  return new Intl.DateTimeFormat(intlLocale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Tokyo',
  }).format(date);
};

export const useEventMessage = (
  event: ChatEvent | undefined,
  ageGroup: AgeGroup
): ResolvedEventMessage | null => {
  const { t, i18n } = useTranslation();

  if (!event) return null;

  const ageKey = ageGroupKey(ageGroup);
  const resetTime = formatResetTime(event.params.reset_at, i18n.language);
  const limit = event.params.limit ?? '';
  const variant = VARIANT_BY_TYPE[event.type];

  switch (event.type) {
    case 'rate_limit_hourly':
      return {
        title: t(`chat.events.rateLimitHourly.${ageKey}`, { limit, resetTime }),
        note: t(`chat.events.rateLimitHourly.note.${ageKey}`),
        variant,
      };
    case 'rate_limit_monthly':
      return {
        title: t(`chat.events.rateLimitMonthly.${ageKey}`, { limit }),
        variant,
      };
    case 'content_filter_input':
      return {
        title: t(`chat.events.contentFilterInput.${ageKey}`),
        variant,
      };
    case 'content_filter_output':
      return {
        title: t(`chat.events.contentFilterOutput.${ageKey}`),
        variant,
      };
    case 'guardrails':
      return {
        title: t(`chat.events.guardrails.${ageKey}`),
        variant,
      };
    case 'generation_error':
      return {
        title: t(`chat.events.generationError.${ageKey}`),
        variant,
      };
    default:
      return null;
  }
};
